// 2026-06-30 - FormuLearning - 관리자: 기간별 호출 횟수 집계 (REQ-FN-ADMIN-02)
// 파일 위치: frontend/src/features/admin/CallStats.tsx

import { useEffect, useMemo, useState } from 'react'
import { getCallStats } from '../../api/admin'
import type { CallStatsRow } from '../../types/admin'

type Bucket = 'day' | 'week' | 'month'
const BUCKETS: Bucket[] = ['day', 'week', 'month']

export default function CallStats() {
  const [bucket, setBucket] = useState<Bucket>('day')
  const [rows, setRows] = useState<CallStatsRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    getCallStats({ bucket })
      .then(setRows)
      .catch(() => setError('Failed to load stats'))
      .finally(() => setLoading(false))
  }, [bucket])

  const maxCount = useMemo(() => Math.max(1, ...rows.map((r) => r.count)), [rows])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-text-secondary">Bucket:</span>
        {BUCKETS.map((b) => (
          <button
            key={b}
            onClick={() => setBucket(b)}
            className="rounded px-2.5 py-1 text-xs capitalize transition-colors"
            style={
              bucket === b
                ? { backgroundColor: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }
                : { color: 'var(--color-text-secondary)' }
            }
          >
            {b}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-text-secondary">Loading…</p>
      ) : error ? (
        <p className="text-sm text-accent-purple">{error}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-text-secondary">No calls recorded yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--border-divider)' }}>
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-text-secondary" style={{ backgroundColor: 'var(--color-bg-panel)' }}>
              <tr>
                <th className="px-3 py-2 font-medium">Period</th>
                <th className="px-3 py-2 font-medium">API</th>
                <th className="px-3 py-2 font-medium">Provider</th>
                <th className="px-3 py-2 font-medium">Calls</th>
                <th className="px-3 py-2 font-medium">OK</th>
                <th className="px-3 py-2 font-medium">Err</th>
                <th className="px-3 py-2 font-medium">Avg ms</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.bucket}-${r.api_id}-${i}`} className="border-t" style={{ borderColor: 'var(--border-divider)' }}>
                  <td className="px-3 py-2 text-text-secondary">{r.bucket.slice(0, 10)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-text-primary">{r.api_code}</td>
                  <td className="px-3 py-2 text-text-body">{r.provider}</td>
                  <td className="px-3 py-2 text-text-body">
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-2 rounded" style={{ width: `${(r.count / maxCount) * 80 + 4}px`, backgroundColor: 'var(--color-accent-teal)' }} />
                      {r.count}
                    </div>
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--color-accent-teal)' }}>{r.success_count}</td>
                  <td className="px-3 py-2" style={{ color: r.error_count > 0 ? 'var(--color-accent-purple)' : 'var(--color-text-secondary)' }}>{r.error_count}</td>
                  <td className="px-3 py-2 text-text-secondary">{r.avg_duration_ms != null ? Math.round(r.avg_duration_ms) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
